'use client';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ProductCard from './ProductCard';
import { useProducts } from '../../context/ProductsContext';
import { useCategories } from '../../context/CategoryContext';

const ProductsMain = ({ filters, sortBy, setSortBy }) => {
  const { products: allProducts, loading, error } = useProducts();
  const { categories } = useCategories();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);


  // ✅ Simplified filter logic (matches your backend structure)
  const filteredProducts = useMemo(() => {
    if (!allProducts || allProducts.length === 0) return [];

    let filtered = allProducts;
    // Category / Subcategory
    if (filters.categories?.length > 0) {
      filtered = filtered.filter(product => {
        const category = product.category?.toLowerCase() || '';
        const subcategory = product.raw.sub_cat_name?.toLowerCase() || '';
        const product_category_name = product.raw.product_category_name?.toLowerCase() || '';
        return filters.categories.some(cat => {
          const selected = cat.toLowerCase();
          return category === selected || subcategory === selected || product_category_name === selected;
        });
      });
    }

    
    // Color
    if (filters.colors?.length > 0) {
      filtered = filtered.filter(product => {
        const color = product.color?.toLowerCase() || '';
        return filters.colors.some(c => color.includes(c.toLowerCase()));
      });
    }

    // Fabric
    if (filters.fabrics?.length > 0) {
      filtered = filtered.filter(product => {
        const fabric = product.fabric?.toLowerCase() || '';
        return filters.fabrics.some(f => fabric.includes(f.toLowerCase()));
      });
    }

    // Price
    if (filters.priceRange?.length === 2) {
      filtered = filtered.filter(product => {
        const price = Number(product.priceValue || product.price || 0);
        return price >= filters.priceRange[0] && price <= filters.priceRange[1];
      });
    }

    return filtered;
  }, [allProducts, filters]);

  // ✅ Sorting
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.priceValue - b.priceValue);
      case 'price-high':
        return sorted.sort((a, b) => b.priceValue - a.priceValue);
      case 'newest':
        return sorted.sort((a, b) =>
          new Date(b.createdAt) - new Date(a.createdAt)
        );
      default:
        return sorted;
    }
  }, [filteredProducts, sortBy]);

  // Pagination
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Reset pagination when filters change
  useEffect(() => setCurrentPage(1), [filters]);

  // Pagination rendering helper
  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) pages.push(1, 2, 3, '...', totalPages);
      else if (currentPage >= totalPages - 2)
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      else
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }

    return pages.map((page, index) =>
      page === '...' ? (
        <span key={index} className="px-2">...</span>
      ) : (
        <button
          key={index}
          onClick={() => handlePageChange(page)}
          className={`px-3 py-1 rounded ${
            currentPage === page
              ? 'bg-yellow-100 text-yellow-700 font-semibold'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {page}
        </button>
      )
    );
  };

  if (error) {
    return (
      <div className="text-center py-16 text-red-500 font-medium">
        Error loading products: {error}
      </div>
    );
  }

  const totalFilters =
    (filters.categories?.length || 0) +
    (filters.colors?.length || 0) +
    (filters.fabrics?.length || 0);

  return (
    <main className="flex flex-col flex-1 w-full">
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-lg font-medium text-gray-700">
          {loading ? 'Loading...' : `${sortedProducts.length} Products`}
        </h1>
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="sort-select" className="font-medium text-gray-700">
            Sort By:
          </label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border rounded px-2 py-1 text-yellow-700 focus:ring-2 focus:ring-yellow-400"
          >
            <option value="trending">Trending</option>
            <option value="price-low">Low to High</option>
            <option value="price-high">High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </header>

      {/* Products Grid */}
      <section className="border-t border-gray-200 pt-6">
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading products...</div>
        ) : currentProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
            {currentProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-500">
            No products found — try adjusting your filters.
          </div>
        )}
      </section>

      {/* Pagination */}
      {sortedProducts.length > 0 && totalPages > 1 && (
        <footer className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 border-t border-gray-200 pt-6">
          {/* Items per page */}
          <div className="flex items-center gap-2 text-sm">
            <span>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border rounded px-2 py-1 text-yellow-700 focus:ring-2 focus:ring-yellow-400"
            >
              <option value="12">12</option>
              <option value="24">24</option>
              <option value="48">48</option>
            </select>
          </div>

          {/* Page numbers */}
          <div className="flex items-center gap-2">{renderPaginationNumbers()}</div>
        </footer>
      )}
    </main>
  );
};

export default ProductsMain;
